import { API_BASE } from '../config';
import { Fragment, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './pages.css';
import './SiteManager.css';
import SiteMemoEditor from './SiteMemoEditor';
import CategoryManager from './CategoryManager';



function siteHref(value) {
    if (!value) return null;
    try {
        const url = new URL(/^https?:\/\//i.test(value) ? value : `https://${value}`);
        return ['http:', 'https:'].includes(url.protocol) ? url.href : null;
    } catch { return null; }
}

function SiteDemographics({ site }) {
    const classified = [20, 30, 40, 50, 60, 70].reduce((sum, age) => sum + Number(site[`${age}남`] || 0) + Number(site[`${age}여`] || 0), 0);
    const unclassified = site.unclassified == null ? Math.max(0, Number(site.합계 || 0) - classified - Number(site.vulnerable_total || 0)) : Number(site.unclassified);
    return <div className="site-demographics" id={`site-demographics-${site.site_id}`}>
        <div className="site-demographics-heading"><strong>{site.site_name} · 배정 인원 상세</strong><span>전체 {site.합계 || 0}명</span></div>
        <table aria-label={`${site.site_name} 연령별 배정 인원`}>
            <thead><tr><th scope="col">구분</th><th scope="col">남</th><th scope="col">여</th><th scope="col">합계</th></tr></thead>
            <tbody>
                {[20, 30, 40, 50, 60, 70].map(age => <tr key={age} data-group={age}>
                    <th scope="row">{age === 70 ? '70대 이상' : `${age}대`}</th>
                    <td>{site[`${age}남`] || 0}</td><td>{site[`${age}여`] || 0}</td>
                    <td>{Number(site[`${age}남`] || 0) + Number(site[`${age}여`] || 0)}</td>
                </tr>)}
                <tr className="vulnerability-row"><th scope="row">취약계층</th><td colSpan="2">—</td><td>{site.vulnerable_total || 0}</td></tr>
                {[["low_vision", "저시력"], ["blind", "전맹"], ["unspecified", "유형미지정"]].map(([type, label]) => <tr key={type} data-vulnerability={type}><th scope="row">{label}</th><td colSpan="2">—</td><td>{site[`vulnerable_${type}`] || 0}</td></tr>)}
                {unclassified > 0 && <tr><th scope="row">기타·미분류</th><td>—</td><td>—</td><td>{unclassified}</td></tr>}
            </tbody>
        </table>
        <p className="demographic-note">취약계층은 유형별로만 집계하며 연령·성별 인원에서 제외합니다.{unclassified > 0 && ' 기타·미분류는 일반 평가자 중 20세 미만 또는 연령·성별 정보가 없는 인원입니다.'}</p>
    </div>;
}

const SiteManager = () => {
    const navigate = useNavigate();

    // 목록/통계/폼 상태
    const [sites, setSites] = useState([]);
    const [expandedSiteIds, setExpandedSiteIds] = useState([]);
    const allExpanded = sites.length > 0 && sites.every(site => expandedSiteIds.includes(site.site_id));
    const [categoryStats, setCategoryStats] = useState(() => Object.create(null));
    const [siteName, setSiteName] = useState('');
    const [url, setUrl] = useState('');
    const [category, setCategory] = useState('');
    const [siteType, setSiteType] = useState('web');

    // 수정 선택/폼
    const [editingSiteIds, setEditingSiteIds] = useState([]);
    const [editForms, setEditForms] = useState({});

    // 사업 필터
    const [programs, setPrograms] = useState([]);
    const [selectedProgram, setSelectedProgram] = useState(''); // '' = 전체
    const [programForm, setProgramForm] = useState(null);
    const [programSaving, setProgramSaving] = useState(false);
    const [programError, setProgramError] = useState('');
    const [programNotice, setProgramNotice] = useState('');

    const [categories, setCategories] = useState([]);
    const [categoryError, setCategoryError] = useState('');
    const [categorySaving, setCategorySaving] = useState(false);
    const selectedCategories = categories;
    const categoryNames = [...new Set([...selectedCategories.map(c => c.name), ...Object.keys(categoryStats)])];
    const fetchCategories = async () => {
        try {
            const res = await fetch(`${API_BASE}/api/admin/categories`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
            if (!res.ok) throw new Error('카테고리를 불러오지 못했습니다. 새로고침해 주세요.');
            setCategories(await res.json());
            setCategoryError('');
        } catch (err) { setCategoryError(err.message); }
    };
    useEffect(() => { fetchCategories(); }, []);
    useEffect(() => { setCategory(''); }, [selectedProgram]);
    const categorySaved = async saved => {
        const old = categories.find(c => c.category_id === saved.category_id);
        setCategories(prev => old ? prev.map(c => c.category_id === saved.category_id ? saved : c) : [...prev, saved]);
        if (old && category === old.name) setCategory(saved.name);
        await fetchSites();
    };
    const token = localStorage.getItem('token');
    const categoryDeleted = deleted => {
        setCategories(prev => prev.filter(c => c.category_id !== deleted.category_id));
        if (category === deleted.name) setCategory('');
    };

    const handleLogout = () => {
        localStorage.removeItem('token');
        navigate('/');
    };

    // 체크박스 선택/해제
    const handleCheckboxChange = (siteId, checked) => {
        setSites(prev =>
        prev.map(site =>
            site.site_id === siteId ? { ...site, _checked: checked } : site
        )
        );

        if (checked) {
        const site = sites.find(s => s.site_id === siteId);
        if (!site) return;
        setEditingSiteIds(prev => [...prev, siteId]);
        setEditForms(prev => ({
            ...prev,
            [siteId]: {
            site_name: site.site_name,
            url: site.url,
            category: site.category,
            site_type: site.site_type || 'web'
            }
        }));
        } else {
        setEditingSiteIds(prev => prev.filter(id => id !== siteId));
        setEditForms(prev => {
            const updated = { ...prev };
            delete updated[siteId];
            return updated;
        });
        }
    };

    const updateEditForm = (siteId, field, value) => {
        setEditForms(prev => ({
        ...prev,
        [siteId]: {
            ...prev[siteId],
            [field]: value
        }
        }));
    };

    // 일괄 저장
    const handleBulkSaveSites = async () => {
        try {
        for (const siteId of editingSiteIds) {
            const form = editForms[siteId];
            const res = await fetch(`${API_BASE}/api/admin/sites/${siteId}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`
            },
            body: JSON.stringify(form)
            });
            if (!res.ok) { const data = await res.json(); throw new Error(data.error || '사이트 수정 실패'); }
        }
        alert('모든 사이트 수정 완료!');
        setEditingSiteIds([]);
        setEditForms({});
        await fetchSites(); // 최신 목록
        } catch (err) {
        console.error('사이트 수정 오류:', err);
        alert('수정 중 오류 발생');
        }
    };

    // 일괄 삭제
    const handleBulkDeleteSites = async () => {
        const confirmed = window.confirm(
        '선택한 사이트를 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.'
        );
        if (!confirmed) return;

        try {
        for (const siteId of editingSiteIds) {
            await fetch(`${API_BASE}/api/admin/sites/${siteId}`, {
            method: 'DELETE',
            headers: { Authorization: `Bearer ${token}` }
            });
        }
        alert('삭제 완료!');
        setEditingSiteIds([]);
        setEditForms({});
        await fetchSites(); // 최신 목록
        } catch (err) {
        console.error('사이트 삭제 오류:', err);
        alert('삭제 중 오류 발생');
        }
    };

    // 사이트 요약 불러오기 (📌 program_id 필터 적용)
    const fetchSites = async () => {
        try {
        const q = selectedProgram ? `?program_id=${selectedProgram}` : '';
        const res = await fetch(`${API_BASE}/api/admin/sites/summary${q}`, {
            headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) {
            const msg = await res.text();
            throw new Error(msg || `HTTP ${res.status}`);
        }
        const data = await res.json();

        // 체크박스 초기화된 목록으로 세팅
        const withFlags = (Array.isArray(data) ? data : []).map(s => ({
            ...s,
            _checked: false
        }));
        setSites(withFlags);
        setExpandedSiteIds([]);

        // 카테고리 집계
        const counts = Object.create(null);
        withFlags.forEach(site => {
            if (!site?.category) return;
            counts[site.category] = (counts[site.category] || 0) + 1;
        });
        setCategoryStats(counts);

        // 선택/수정 폼도 초기화(데이터 바뀌면 꼬이지 않게)
        setEditingSiteIds([]);
        setEditForms({});
        } catch (err) {
        console.error('사이트 목록 로딩 오류:', err);
        setSites([]);
        setCategoryStats(Object.create(null));
        }
    };

    // 사업 목록
    const fetchPrograms = async () => {
        try {
        const res = await fetch(`${API_BASE}/api/programs`, {
            headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) {
            const msg = await res.text();
            throw new Error(msg || `HTTP ${res.status}`);
        }
        const data = await res.json();
        setPrograms(Array.isArray(data) ? data : []);
        } catch (err) {
        console.error('사업 목록 로딩 오류:', err);
        setPrograms([]);
        }
    };

    const openProgramForm = (editing = false) => {
        const program = editing ? programs.find(p => String(p.program_id) === selectedProgram) : null;
        if (editing && !program) return;
        setProgramError('');
        setProgramNotice('');
        setProgramForm({ id: program?.program_id ?? null, name: program?.name ?? '', year: editing ? (program.year ?? '') : new Date().getFullYear() });
    };

    const saveProgram = async (event) => {
        event.preventDefault();
        if (programSaving) return;
        const name = programForm.name.trim();
        const year = programForm.year === '' ? null : Number(programForm.year);
        if (!name) return setProgramError('사업명을 입력해 주세요.');
        if (year !== null && (!Number.isInteger(year) || year < 1000 || year > 9999)) {
            return setProgramError('연도는 네 자리 숫자로 입력해 주세요.');
        }
        setProgramSaving(true);
        setProgramError('');
        try {
            const editing = programForm.id !== null;
            const res = await fetch(`${API_BASE}/api/admin/programs${editing ? `/${programForm.id}` : ''}`, {
                method: editing ? 'PUT' : 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ name, year })
            });
            const result = await res.json().catch(() => ({}));
            if (!res.ok || !result.success) throw new Error(result.error || '사업 저장에 실패했습니다.');
            const saved = { program_id: result.program_id, name: result.name, year: result.year };
            setPrograms(prev => editing
                ? prev.map(p => String(p.program_id) === String(saved.program_id) ? { ...p, ...saved } : p)
                : [saved, ...prev]);
            setSelectedProgram(String(saved.program_id));
            setProgramForm(null);
            setProgramNotice(editing ? '사업이 수정되었습니다.' : '사업이 추가되었습니다.');
            await fetchCategories();
        } catch (err) {
            setProgramError(err.message);
        } finally {
            setProgramSaving(false);
        }
    };

    // 사이트 등록 (현재 선택된 사업으로)
    const handleSiteRegister = async () => {
        if (!selectedProgram) {
        alert('먼저 사업을 선택해주세요.');
        return;
        }
        if (!siteName || !url || !category) {
        alert('사이트명/URL/카테고리를 모두 입력해 주세요.');
        return;
        }

        try {
        const body = {
            program_id: Number(selectedProgram),
            site_name: siteName,
            url,
            category,
            site_type: siteType
        };
        const res = await fetch(`${API_BASE}/api/admin/sites`, {
            method: 'POST',
            headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
            },
            body: JSON.stringify(body)
        });
        const result = await res.json();
        if (res.ok && result?.success) {
            alert('등록 완료');
            setSiteName('');
            setUrl('');
            setCategory('');
            setSiteType('web');
            await fetchSites(); // 최신 목록
        } else {
            throw new Error(result?.error || '등록 실패');
        }
        } catch (err) {
        console.error('사이트 등록 오류:', err);
        alert('등록 실패: ' + err.message);
        }
    };

    // 최초 로딩
    useEffect(() => {
        fetchPrograms();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // 📌 사업 선택이 바뀔 때마다 목록 재조회
    useEffect(() => {
        fetchSites();
        // 배경 클래스 유지
        document.body.classList.add('result-page-bg');
        return () => {
        document.body.classList.remove('result-page-bg');
        };
    }, [selectedProgram]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div>
        <div className="top-right-button-group">
            <button onClick={() => navigate('/dashboard')} className="navigate-button">대시보드</button>
            <button onClick={() => navigate('/user')} className="navigate-button">
            평가단 관리
            </button>
            <button onClick={() => navigate('/site')} className="navigate-button primary">
            사이트 관리
            </button>
            <button onClick={() => navigate('/admin')} className="navigate-button">
            설문 관리
            </button>
            <button onClick={() => navigate('/responses')} className="navigate-button">
            응답 관리
            </button>
            <button onClick={() => navigate('/results')} className="navigate-button">
            통계 보기
            </button>
            <button onClick={() => navigate('/admin/login-notice')} className="navigate-button">로그인 공지 관리</button>
            <button onClick={handleLogout} className="navigate-button logout-button">
            로그아웃
            </button>
        </div>

        <div className="survey-result-container">
            <h2>사이트 관리 페이지</h2>

            {/* 사업 선택 */}
            <div className="program-toolbar">
            <label htmlFor="program-filter">사업:</label>
            <select
                id="program-filter"
                disabled={programSaving || categorySaving}
                className="input-beauty select-sm-blue"
                value={selectedProgram}
                onChange={(e) => {
                    setSelectedProgram(e.target.value);
                    setProgramForm(null);
                    setProgramError('');
                    setProgramNotice('');
                }}
            >
                <option value="">전체</option>
                {programs.map((p) => (
                <option key={p.program_id} value={String(p.program_id)}>
                    {p.name}
                    {p.year ? ` (${p.year})` : ''}
                </option>
                ))}
            </select>
            <button type="button" className="user-input-button" disabled={programSaving || categorySaving} onClick={() => openProgramForm()}>사업 추가</button>
            <button type="button" className="user-input-button" disabled={programSaving || categorySaving || !selectedProgram} onClick={() => openProgramForm(true)}>사업 수정</button>
            </div>
            {programNotice && <p role="status">{programNotice}</p>}
            {programForm && (
                <form className="program-editor" onSubmit={saveProgram} aria-labelledby="program-editor-title">
                    <h3 id="program-editor-title">{programForm.id === null ? '사업 추가' : '사업 수정'}</h3>
                    <fieldset disabled={programSaving || categorySaving}>
                        <label htmlFor="program-name">사업명
                            <input autoFocus id="program-name" name="programName" className="user-input" required value={programForm.name} onChange={e => setProgramForm({ ...programForm, name: e.target.value })} />
                        </label>
                        <label htmlFor="program-year">연도 (선택)
                            <input id="program-year" name="programYear" className="user-input" type="number" min="1000" max="9999" step="1" value={programForm.year} onChange={e => setProgramForm({ ...programForm, year: e.target.value })} />
                        </label>
                        <div className="program-editor-actions">
                            <button type="button" className="user-input-button" onClick={() => { setProgramForm(null); setProgramError(''); }}>취소</button>
                            <button type="submit" className="user-input-button program-save-button">{programSaving ? '저장 중…' : '저장'}</button>
                        </div>
                    </fieldset>
                    {programError && <p className="program-error" role="alert">{programError}</p>}
                </form>
            )}

            <CategoryManager disabled={programSaving} categories={selectedCategories} onSaved={categorySaved} onDeleted={categoryDeleted} onBusyChange={setCategorySaving} />
            {categoryError && <p role="alert" className="program-error">{categoryError}</p>}
            {/* 등록 현황 */}
            <div className="table-container">
            <h3>1. 사이트 등록 현황</h3>
            <table className="user-styled-table">
                <thead>
                <tr>
                    <th>카테고리</th>
                    <th>등록 수</th>
                </tr>
                </thead>
                <tbody>
                {categoryNames.map(name => (
                    <tr key={name}><td>{name}</td><td>{Object.hasOwn(categoryStats, name) ? categoryStats[name] : 0}</td></tr>
                ))}
                </tbody>
            </table>
            </div>

            {/* 사이트 목록 */}
            <div className="table-container">
            <div className="site-list-heading"><div><h3>2. 사이트 목록</h3><p>사이트별 배정 현황을 확인하고, 상세를 펼쳐 연령별 인원을 비교하세요.</p></div>
                <button type="button" className="site-detail-toggle" disabled={!sites.length} onClick={() => setExpandedSiteIds(allExpanded ? [] : sites.map(site => site.site_id))}>{allExpanded ? '상세 전체 접기' : '상세 전체 펼치기'}</button>
            </div>
            <div className="site-list-scroll">
                <table className="user-styled-table fixed-table site-list-table">
                    <colgroup><col style={{width:'4%'}} /><col style={{width:'5%'}} /><col style={{width:'31%'}} /><col style={{width:'15%'}} /><col style={{width:'11%'}} /><col style={{width:'10%'}} /><col style={{width:'10%'}} /><col style={{width:'14%'}} /></colgroup>
                    <thead><tr><th aria-label="수정 선택"></th><th>순번</th><th>사이트명 / URL</th><th>카테고리</th><th className="site-type-heading">구분(앱/웹)</th><th>배정 인원</th><th>취약계층</th><th>상세</th></tr></thead>
                <tbody>
                {sites.map((site, i) => {
                    const isEditing = editingSiteIds.includes(site.site_id);
                    const expanded = expandedSiteIds.includes(site.site_id);
                    const form = editForms[site.site_id] || {};

                    return (
                    <Fragment key={site.site_id}>
                    <tr className={expanded ? "site-summary-row is-expanded" : "site-summary-row"}>
                        <td>
                        <input
                            className="user-input"
                            type="checkbox"
                            checked={site._checked || false}
                            onChange={(e) =>
                            handleCheckboxChange(site.site_id, e.target.checked)
                            }
                        />
                        </td>
                        <td>{i + 1}</td>

                        {isEditing ? (
                        <>
                            <td>
                            <input
                                className='user-input'
                                value={form.site_name || ''}
                                onChange={(e) =>
                                updateEditForm(site.site_id, 'site_name', e.target.value)
                                }
                            />
                            <input
                                aria-label="사이트 URL"
                                className='user-input'
                                value={form.url || ''}
                                onChange={(e) =>
                                updateEditForm(site.site_id, 'url', e.target.value)
                                }
                            />
                            </td>
                            <td>
                            <select
                                className='user-input'
                                value={form.category || ''}
                                onChange={(e) =>
                                updateEditForm(site.site_id, 'category', e.target.value)
                                }
                            >
                                {[...new Set([site.category, ...categories.map(c => c.name)])].filter(Boolean).map(name => <option key={name} value={name}>{name}</option>)}
                            </select>
                            </td>
                            <td>
                            <select className="user-input site-type-select" aria-label="구분(앱/웹)" value={form.site_type || 'web'} onChange={e => updateEditForm(site.site_id, 'site_type', e.target.value)}>
                                <option value="web">웹</option>
                                <option value="app">앱</option>
                            </select>
                            </td>
                        </>
                        ) : (
                        <>
                            <td className="site-identity"><strong>{site.site_name}</strong>{siteHref(site.url) ? <a href={siteHref(site.url)} title={site.url} target="_blank" rel="noopener noreferrer">{site.url}</a> : <span>{site.url}</span>}<SiteMemoEditor site={site} onSaved={(id, memo) => setSites(previous => previous.map(item => item.site_id === id ? { ...item, ...memo } : item))} /></td>
                            <td>{site.category}</td>
                            <td><span className={`site-type-badge site-type-${site.site_type || 'web'}`}>{site.site_type === 'app' ? '앱' : '웹'}</span></td>
                        </>
                        )}

                        <td className="site-count">{site.합계 || 0}<span>명</span></td>
                        <td className="site-count">{site.vulnerable_total || 0}<span>명</span></td>
                        <td><button type="button" className="site-detail-toggle" aria-expanded={expanded} aria-controls={`site-demographics-${site.site_id}`} aria-label={`${site.site_name} 인원 상세 ${expanded ? '접기' : '펼치기'}`} onClick={() => setExpandedSiteIds(prev => expanded ? prev.filter(id => id !== site.site_id) : [...prev, site.site_id])}>{expanded ? '상세 접기 ▴' : '인원 상세 ▾'}</button></td>
                    </tr>
                    {expanded && <tr className="site-detail-row"><td colSpan="8"><SiteDemographics site={site} /></td></tr>}
                    </Fragment>
                    );
                })}
                </tbody>
            </table>
            </div>

            {editingSiteIds.length > 0 && (
                <div style={{ marginTop: '16px', display: 'flex', gap: '8px' }}>
                <button className="user-input-button" onClick={handleBulkSaveSites}>
                    일괄 저장
                </button>
                <button
                    className="user-input-button"
                    style={{ backgroundColor: '#ffdddd', color: 'red' }}
                    onClick={handleBulkDeleteSites}
                >
                    삭제
                </button>
                </div>
            )}
            </div>

            {/* 사이트 등록 */}
            <div>
            <h3>3. 사이트 등록</h3>
            </div>
            <div className="user-input-row site-register-row">
            <input
                className="user-input"
                placeholder="사이트명"
                value={siteName}
                onChange={(e) => setSiteName(e.target.value)}
            />
            <input
                className="user-input"
                placeholder="URL"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
            />
            <select
                className="user-input"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
            >
                <option value="">카테고리 선택</option>
                {selectedCategories.map(c => <option key={c.category_id} value={c.name}>{c.name}</option>)}
            </select>
            <label className="site-type-field">
                <span>구분(앱/웹)</span>
                <select className="user-input site-type-select" aria-label="구분(앱/웹)" value={siteType} onChange={e => setSiteType(e.target.value)}>
                    <option value="web">웹</option>
                    <option value="app">앱</option>
                </select>
            </label>
            <button className="user-input-button" onClick={handleSiteRegister}>
                등록하기
            </button>
            </div>
        </div>
        </div>
    );
};

export default SiteManager;
